import { withErrorBoundary } from 'app/components/error-boundary';
import type { NewScreenParams } from 'app/screens/new';
import type { HubTab } from './components';
import type { NavigationScreen } from 'app/types/navigation';
import { View } from 'react-native';
import { HubTutorial, styles } from './components';
import { OfferHubSuggestionsTab } from './components/offer-hub-suggestions-tab';
import { OfferHubHeader } from './components/offer-hub-header';
import { OfferHubStoriesTab } from './components/offer-hub-stories-tab';
import { CreationTabContext } from 'app/screens/new/creation-tab-context';
import { OfferHubManualTab } from './components/offer-hub-manual-tab';
import { OfferHubPhotoTab } from './components/offer-hub-photo-tab';

import type { ComponentProps } from 'react';
import { useOfferHubState } from './hooks';

export type OfferHubScreenParams = NewScreenParams & { tab?: HubTab };

function OfferHubScreenComponent(props: ComponentProps<NavigationScreen<'offer-hub' | 'publish'>>) {
  const state = useOfferHubState(props);
  const {
    userQuery,
    hasAccess,
    insets,
    tab,
    tutorialOverlayVisible,
    tutorialStep,
    canUseAI,
    canUseStories,
    canUseSuggestions,
    centralizedCreation,
    setTutorialStep,
    dismissTutorial,
    visitedTabs,
    focused,
    bulkRoute,
    manualRoute,
    navigation,
    creationRequest,
    setActive,
    setTab,
    t,
    helpTutorialVisible,
    swiping,
    replayTutorial,
    items,
    round,
    selectTab,
    replaySwipeTutorial,
    close,
    completed,
  } = state;
  if (userQuery.isPending || !hasAccess) return null;
  return (
    <View style={[styles.screen, { paddingTop: insets.top, paddingBottom: tab === 'suggestions' ? insets.bottom : 0 }]}>
      <View
        style={styles.container}
        accessibilityElementsHidden={tutorialOverlayVisible}
        importantForAccessibility={tutorialOverlayVisible ? 'no-hide-descendants' : 'auto'}
      >
        <OfferHubHeader
          completed={completed}
          helpTutorialVisible={helpTutorialVisible}
          t={t}
          swiping={swiping}
          setActive={setActive}
          replayTutorial={replayTutorial}
          items={items}
          round={round}
          tutorialOverlayVisible={tutorialOverlayVisible}
          tab={tab}
          selectTab={selectTab}
          canUseAI={canUseAI}
          canUseStories={canUseStories}
          canUseSuggestions={canUseSuggestions}
          replaySwipeTutorial={replaySwipeTutorial}
          close={close}
        />
        {visitedTabs.includes('photos') && canUseAI ? (
          <OfferHubPhotoTab tab={tab} bulkRoute={bulkRoute} navigation={navigation} focused={focused} />
        ) : null}
        {visitedTabs.includes('manual') ? (
          <CreationTabContext.Provider value={props.route.name === 'publish'}>
            <OfferHubManualTab
              tab={tab}
              manualRoute={manualRoute}
              creationRequest={creationRequest}
              navigation={navigation}
              setActive={setActive}
              setTab={setTab}
              centralizedCreation={centralizedCreation}
            />
          </CreationTabContext.Provider>
        ) : null}
        {visitedTabs.includes('stories') && canUseStories ? (
          <OfferHubStoriesTab active={tab === 'stories' && focused} visible={tab === 'stories'} />
        ) : null}
        <OfferHubSuggestionsTab {...state} />
      </View>
      {tutorialOverlayVisible ? (
        <HubTutorial
          step={tutorialStep}
          topInset={insets.top}
          canUseAI={canUseAI}
          onStepChange={setTutorialStep}
          onDismiss={dismissTutorial}
        />
      ) : null}
    </View>
  );
}
export const OfferHubScreen = withErrorBoundary(OfferHubScreenComponent);
